import { ChangeEvent, useMemo, useState } from 'react';
import {
  Box,
  Modal,
  Text,
  Icon,
  IconButton,
  Link,
  Alert,
} from '@nimbus-ds/components';
import {
  CheckCircleIcon,
  MoneyIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@nimbus-ds/icons';
import { CurrencyType } from '@tiendanube/common';
import { CancelAndConfirmButtons, InputNumberNimbus } from 'commons/components';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { DiscountRow } from './DiscountRow';
import { useCanEditDiscounts } from '../../../hooks';
import { OrderEditDiscount } from '../../Products';
import { OrderEditDiscountType } from '../../Products/types';

interface DiscountEditModalProps {
  show: boolean;
  closeModal: () => void;
  discounts: OrderEditDiscount[];
  updateDiscounts: (discounts: OrderEditDiscount[]) => void;
  maxDiscount: number;
  currency: CurrencyType;
}

function DiscountEditModal({
  show,
  closeModal,
  discounts,
  updateDiscounts,
  maxDiscount,
  currency,
}: Readonly<DiscountEditModalProps>) {
  const t = useTranslationOrders();
  const [couponReplaced, setCouponReplaced] = useState(false);
  const [editedDiscounts, setEditedDiscounts] = useState(discounts);

  const totalDiscount = useMemo(
    () =>
      editedDiscounts
        .filter((d) => d.editType !== 'remove')
        .reduce((acc, discount) => acc + discount.value, 0),
    [editedDiscounts],
  );

  const initialDiscountValue = useMemo(
    () => discounts.find((d) => d.id === 'editCouponDiscount')?.value,
    [discounts],
  );

  const existingCouponDiscount = useMemo(
    () => discounts.find((d) => d.id === 'couponDiscount'),
    [discounts],
  );

  const [discount, setDiscount] = useState<string>(
    initialDiscountValue?.toString() ?? '',
  );
  const [hasError, setHasError] = useState(false);
  const discountNumber = discount !== '' ? Number(discount) : undefined;
  const [showEditDiscountInput, setShowEditDiscountInput] = useState(false);
  const canEditDiscounts = useCanEditDiscounts();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newDiscountValue = e.target.value;
    const newDiscountValueNumber =
      newDiscountValue !== '' ? Number(newDiscountValue) : undefined;
    setHasError(
      newDiscountValueNumber !== undefined &&
        (newDiscountValueNumber === 0 ||
          Number(newDiscountValueNumber) > maxDiscount),
    );
    setDiscount(newDiscountValue);
  };

  const handleConfirm = () => {
    const discountValueNumber = discount !== '' ? Number(discount) : undefined;
    const updatedDiscounts = editedDiscounts.filter(
      (d) => d.id !== 'editCouponDiscount',
    );
    if (discountValueNumber !== undefined) {
      updatedDiscounts.push({
        id: 'editCouponDiscount',
        discountType: 'coupon_discount',
        editType: 'add',
        value: discountValueNumber ?? 0,
        removed: false,
        name: t('editOrders.discountModal.customDiscount'),
        appearance: 'danger',
        prefix: '-',
      });
    }
    updateDiscounts(updatedDiscounts);
    closeModal();
  };

  const handleRemove = () => {
    const updatedDiscounts = editedDiscounts.map((d) => {
      if (d.id === existingCouponDiscount?.id) {
        return {
          ...d,
          editType: undefined,
        };
      }

      if (d.id === 'editCouponDiscount') {
        return {
          ...d,
          editType: 'remove' as OrderEditDiscountType,
        };
      }

      return d;
    });
    setEditedDiscounts(updatedDiscounts);
    if (existingCouponDiscount) {
      setCouponReplaced(false);
    }
    setDiscount('');
    setShowEditDiscountInput(false);
    setHasError(false);
    if (!canEditDiscounts) {
      updateDiscounts(updatedDiscounts);
      closeModal();
    }
  };

  const handleCloseModal = () => {
    setDiscount(initialDiscountValue?.toString() ?? '');
    setHasError(false);
    closeModal();
  };

  const editDiscount = discounts.find(
    (discount) => discount.id === 'editCouponDiscount',
  );

  const discountsEdited = useMemo(
    () =>
      editedDiscounts.some((ed) => {
        const originalDiscount = discounts.find((d) => d.id === ed.id);
        return ed.editType !== originalDiscount?.editType;
      }),
    [discounts, editedDiscounts],
  );

  const isConfirmDisabled =
    hasError || (!discountsEdited && !showEditDiscountInput && discount === '');

  const handleCheckboxChange = (
    checked: boolean,
    discountId: string,
    discountType: string,
  ) => {
    const newEditType = checked ? undefined : 'remove';

    if (discountId === 'promotions') {
      setEditedDiscounts(
        editedDiscounts.map((discount) => {
          if (discount.discountType === 'promotional_discount') {
            return {
              ...discount,
              editType: newEditType,
            };
          }
          return discount;
        }),
      );
    } else {
      setEditedDiscounts(
        editedDiscounts.map((d) => {
          if (d.id === discountId && d.discountType === discountType) {
            return {
              ...d,
              editType: newEditType,
            };
          }
          return d;
        }),
      );
    }
  };

  const handleLinkClick = () => {
    setShowEditDiscountInput(true);
    if (existingCouponDiscount) {
      setEditedDiscounts(
        editedDiscounts.map((d) => {
          if (
            d.id === existingCouponDiscount.id &&
            d.discountType === existingCouponDiscount.discountType
          ) {
            return {
              ...d,
              editType: 'remove',
            };
          }
          return d;
        }),
      );
      setCouponReplaced(true);
    }
  };

  const nonPromotionalDiscounts = editedDiscounts.filter(
    (d) =>
      d.discountType !== 'promotional_discount' &&
      (canEditDiscounts || d.id !== 'editCouponDiscount'),
  );
  const promotionalDiscounts = editedDiscounts.filter(
    (d) => d.discountType === 'promotional_discount',
  );
  const promotionsSelected = promotionalDiscounts.some(
    (d) => d.editType !== 'remove',
  );
  const totalPromotionsValue = promotionalDiscounts.reduce(
    (acc, p) => acc + p.value,
    0,
  );

  return (
    <Modal open={show} onDismiss={handleCloseModal}>
      <Modal.Header
        title={
          canEditDiscounts || editDiscount
            ? t('editOrders.discountModal.titleEdit')
            : t('editOrders.discountModal.titleAdd')
        }
      />
      <Modal.Body padding="none">
        {couponReplaced && (
          <Box marginBottom="2">
            <Alert appearance="warning">
              {t('editOrders.discountModal.alert', {
                couponName: existingCouponDiscount?.name,
              })}
            </Alert>
          </Box>
        )}
        <Box display="flex" flexDirection="column" gap="4">
          <Text color="neutral-textLow">
            {canEditDiscounts && discounts.length > 0
              ? t('editOrders.discountModal.descriptionPlural')
              : t('editOrders.discountModal.description')}
          </Text>
          {canEditDiscounts &&
            nonPromotionalDiscounts.map((discount) => (
              <DiscountRow
                key={discount.id}
                id={discount.id}
                type={discount.discountType}
                name={discount.name}
                value={discount.value}
                handleCheckboxChange={handleCheckboxChange}
                currency={currency}
                checked={discount.editType !== 'remove'}
                disabled={
                  discount.discountType === 'coupon_discount' &&
                  showEditDiscountInput
                }
                withCheckbox
              />
            ))}
          {canEditDiscounts && promotionalDiscounts.length > 0 && (
            <DiscountRow
              id="promotions"
              type="promotions"
              name={t('editOrders.discountModal.promotions')}
              value={totalPromotionsValue}
              handleCheckboxChange={handleCheckboxChange}
              currency={currency}
              checked={promotionsSelected}
              withCheckbox
            />
          )}
          {canEditDiscounts &&
            promotionalDiscounts.map((promotion) => (
              <Box paddingLeft="6" key={promotion.id}>
                <Text color="neutral-textHigh">{promotion.name}</Text>
              </Box>
            ))}
          {(!canEditDiscounts || showEditDiscountInput) && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box paddingTop="2">
                <Text color="neutral-textHigh">
                  {t('editOrders.discountModal.customDiscount')}
                </Text>
              </Box>
              <Box
                display="flex"
                gap="1"
                alignItems="flex-start"
                maxWidth="160px"
              >
                <Box>
                  <InputNumberNimbus
                    appearance={hasError ? 'danger' : 'neutral'}
                    type="float2d"
                    onChange={handleInputChange}
                    append={<Icon source={<MoneyIcon color="grey" />} />}
                    defaultDecimals={2}
                    value={discount}
                  />
                  {hasError && (
                    <Box display="flex" gap="0-5" paddingTop="1">
                      <Icon
                        color="danger-textLow"
                        source={<CheckCircleIcon size="small" />}
                      />
                      <Text
                        as="span"
                        color="danger-textLow"
                        fontSize="caption"
                        lineHeight="caption"
                      >
                        {discountNumber && discountNumber > maxDiscount
                          ? t('editOrders.discountModal.errorMaxCaption')
                          : t('editOrders.discountModal.errorCaption')}
                      </Text>
                    </Box>
                  )}
                </Box>
                {(editDiscount || canEditDiscounts) && (
                  <Box width="32px">
                    <IconButton
                      source={<TrashIcon />}
                      onClick={handleRemove}
                      size="2rem"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {canEditDiscounts && (
            <Box
              borderStyle="solid"
              borderTopWidth="1"
              borderBottomWidth="1"
              borderColor="neutral-surfaceDisabled"
              paddingY="2"
            >
              <DiscountRow
                id="totalDiscounts"
                type="totalDiscounts"
                name={t('editOrders.discountModal.totalDiscounts')}
                currency={currency}
                value={totalDiscount}
                handleCheckboxChange={handleCheckboxChange}
                checked
              />
            </Box>
          )}
          {canEditDiscounts && !showEditDiscountInput && (
            <Box
              paddingBottom="2"
              paddingLeft="2"
              display="flex"
              flexDirection="row"
              gap="2-5"
            >
              <Link
                appearance="primary"
                textDecoration="none"
                onClick={handleLinkClick}
              >
                <Icon color="primary-interactive" source={<PlusCircleIcon />} />
                {t('editOrders.summary.addDiscount')}
              </Link>
            </Box>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <CancelAndConfirmButtons
          onCancel={handleCloseModal}
          onConfirm={handleConfirm}
          isConfirmDisabled={isConfirmDisabled}
          confirmText={
            canEditDiscounts || editDiscount
              ? t('editOrders.discountModal.editButton')
              : t('editOrders.discountModal.addButton')
          }
        />
      </Modal.Footer>
    </Modal>
  );
}

export default DiscountEditModal;
